import type { Config } from '@/config';
import { AuthenticationError, NetworkError } from '@/types';
import { VERSION } from '@/version';

// Endpoint for SAS token
const SAS_ENDPOINT = '/api/storage/token';

/**
 * Response structure for SAS token request (based on actual API response)
 */
export interface SasTokenResponse {
  // Token ID for tracking
  tokenId: string;
  // SAS token string (e.g., 'sv=2025-05-05&spr=https&st=...')
  sasToken: string;
  // Container URL (e.g., 'https://testreportx.blob.core.windows.net/staging-data')
  containerUrl: string;
  // Path prefix for uploads (e.g., '2025/06/16/mbywj9boxoxqogznm')
  blobPath: string;
  // Unique identifier
  uniqueId: string;
  // Expiration timestamp ISO
  expiresAt: string;
  // Maximum file size
  maxSize: number;
  // Permissions array
  permissions: string[];
  // Allowed file types for upload
  allowedFileTypes: string[];
}

/**
 * Internal wrapper for SAS token HTTP response
 */
interface RawSasResponse {
  success?: unknown;
  data?: unknown;
  message?: unknown;
}

/**
 * Service to request and manage Azure SAS tokens from TestDino API
 */
export class SasTokenService {
  private readonly baseUrl: string;

  private readonly apiKey: string;

  constructor(private readonly config: Config) {
    this.baseUrl = config.apiUrl;
    this.apiKey = config.token;
  }

  /**
   * Headers to include on every SAS token request
   */
  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'User-Agent': `tdpw/${VERSION}`,
      'X-API-Key': this.apiKey, // Fixed: was 'X-API-key', now matches curl
    };
  }

  /**
   * Request a SAS token from TestDino API using POST method
   */
  async requestSasToken(): Promise<SasTokenResponse> {
    const url = `${this.baseUrl}${SAS_ENDPOINT}`;
    let response: Response;

    try {
      // Fixed: Use POST method with proper headers as shown in curl
      response = await fetch(url, {
        method: 'POST', // Changed from GET to POST
        headers: this.getHeaders(),
        // Empty body for POST request
        body: JSON.stringify({}),
      });
    } catch (error) {
      throw new NetworkError(
        `Network error requesting SAS token: ${(error as Error).message}`
      );
    }

    // Handle HTTP error responses
    if (!response.ok) {
      await this.handleSasError(response);
    }

    // Parse JSON response
    let data: unknown;
    try {
      data = await response.json();
    } catch (error) {
      throw new NetworkError(
        `Failed to parse SAS token response: ${(error as Error).message}`
      );
    }

    // Validate and extract SAS token data
    return this.validateSasResponse(data);
  }

  /**
   * Handle HTTP error responses with detailed error messages
   */
  private async handleSasError(response: Response): Promise<never> {
    let errorBody: string;
    try {
      errorBody = await response.text();
    } catch {
      errorBody = 'Unable to read error response';
    }

    switch (response.status) {
      case 401:
        throw new AuthenticationError('Invalid API key for SAS token request');

      case 403:
        throw new AuthenticationError(
          'API key does not have permission to request SAS tokens'
        );

      case 400:
        throw new NetworkError(`Bad SAS token request: ${errorBody}`);

      case 404:
        throw new NetworkError(
          `SAS token endpoint not found (${response.status}): ${errorBody}`
        );

      case 429:
        throw new NetworkError('Rate limit exceeded for SAS token requests');

      case 500:
      case 502:
      case 503:
      case 504:
        throw new NetworkError(
          `Server error requesting SAS token (${response.status}) - please try again later`
        );

      default:
        throw new NetworkError(
          `SAS token request failed: HTTP ${response.status} - ${errorBody}`
        );
    }
  }

  /**
   * Validate and parse the SAS token response (updated for actual API format)
   */
  private validateSasResponse(data: unknown): SasTokenResponse {
    if (!data || typeof data !== 'object') {
      throw new NetworkError(
        'Invalid SAS token response format - expected JSON object'
      );
    }

    const response = data as RawSasResponse;

    // Check for success wrapper format
    if (
      response.success !== true ||
      response.data == null ||
      typeof response.data !== 'object'
    ) {
      const message =
        typeof response.message === 'string'
          ? response.message
          : 'Unknown error';
      throw new NetworkError(`SAS token request failed: ${message}`);
    }

    const sasData = response.data as Record<string, unknown>;

    // Validate required fields based on actual API response
    const requiredFields = [
      'tokenId',
      'sasToken',
      'containerUrl',
      'blobPath',
      'uniqueId',
      'expiresAt',
    ];
    const missingFields = requiredFields.filter(field => !(field in sasData));

    if (missingFields.length > 0) {
      throw new NetworkError(
        `SAS token response missing required fields: ${missingFields.join(', ')}`
      );
    }

    // Validate field types
    if (typeof sasData.tokenId !== 'string') {
      throw new NetworkError('SAS token response: tokenId must be a string');
    }

    if (typeof sasData.sasToken !== 'string') {
      throw new NetworkError('SAS token response: sasToken must be a string');
    }

    if (typeof sasData.containerUrl !== 'string') {
      throw new NetworkError(
        'SAS token response: containerUrl must be a string'
      );
    }

    if (typeof sasData.expiresAt !== 'string') {
      throw new NetworkError('SAS token response: expiresAt must be a string');
    }

    // Validate container URL format
    try {
      new URL(sasData.containerUrl as string);
    } catch {
      throw new NetworkError(
        `Invalid containerUrl format: ${sasData.containerUrl}`
      );
    }

    const result: SasTokenResponse = {
      tokenId: sasData.tokenId as string,
      sasToken: sasData.sasToken as string,
      containerUrl: sasData.containerUrl as string,
      blobPath: sasData.blobPath as string,
      uniqueId: sasData.uniqueId as string,
      expiresAt: sasData.expiresAt as string,
      maxSize:
        typeof sasData.maxSize === 'number' ? sasData.maxSize : 2147483648, // 2GB default
      permissions: Array.isArray(sasData.permissions)
        ? (sasData.permissions as string[])
        : [],
      allowedFileTypes: Array.isArray(sasData.allowedFileTypes)
        ? (sasData.allowedFileTypes as string[])
        : [],
    };

    return result;
  }

  /**
   * Check if SAS token is expired (updated for actual API format)
   */
  isTokenExpired(sasResponse: SasTokenResponse): boolean {
    try {
      const expirationDate = new Date(sasResponse.expiresAt);
      const now = new Date();

      // Add small buffer (5 minutes) to account for clock skew
      const bufferMs = 5 * 60 * 1000;
      return expirationDate.getTime() - bufferMs <= now.getTime();
    } catch {
      // If we can't parse the date, assume it's expired for safety
      return true;
    }
  }

  /**
   * Get time until SAS token expires (in minutes)
   */
  getTimeUntilExpiry(sasResponse: SasTokenResponse): number {
    try {
      const expirationDate = new Date(sasResponse.expiresAt);
      const now = new Date();
      const diffMs = expirationDate.getTime() - now.getTime();
      return Math.floor(diffMs / (60 * 1000)); // Convert to minutes
    } catch {
      return 0;
    }
  }
}
